"use client";

import { useState, useEffect } from "react";
import { Share2, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FaLinkedinIn, FaXTwitter, FaFacebookF } from "react-icons/fa6";
import { PiShareNetwork } from "react-icons/pi";

interface ShareButtonProps {
    url: string;
    title: string;
    description?: string;
}

export default function ShareButton({ url, title, description }: ShareButtonProps) {
    const [copied, setCopied] = useState(false);

    // Ensure we have the correct URL (use passed URL or fallback to current page URL)
    // Use useState to avoid hydration mismatch
    const [shareUrl, setShareUrl] = useState(url || '');

    // Set the URL on the client side only
    useEffect(() => {
        if (typeof window !== 'undefined' && (!url || !url.startsWith('http'))) {
            setShareUrl(window.location.href);
        }
    }, [url]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error("Failed to copy:", error);
        }
    };

    const defaultPost = `${title}

${description || ""}

Read more here:
`;

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    text: description || title,
                    url: shareUrl,
                });
            } catch (error) {
                // User cancelled or error occurred
                console.error("Share failed:", error);
            }
        } else {
            // Fallback to copy if Web Share API is not available
            handleCopy();
        }
    };

    const shareToX = () => {
        const xUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(defaultPost)}`;
        window.open(xUrl, "_blank", "noopener,noreferrer");
    };

    const shareToLinkedIn = () => {
        const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        window.open(linkedInUrl, "_blank", "noopener,noreferrer");
    };

    const shareToFacebook = () => {
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(defaultPost)}`;
        window.open(facebookUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full size-10" aria-label="Open share menu"
                    aria-haspopup="menu"
                    aria-expanded="false"
                    data-state="closed">
                    <PiShareNetwork size={16} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {typeof navigator !== "undefined" && "share" in navigator && (
                    <DropdownMenuItem onClick={handleShare}>
                        <Share2 size={16} className="mr-2" />
                        Share via...
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleCopy}>
                    {copied ? (
                        <>
                            <Check size={16} className="mr-2" />
                            Copied!
                        </>
                    ) : (
                        <>
                            <Copy size={16} className="mr-2" />
                            Copy link
                        </>
                    )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={shareToX}>
                    <FaXTwitter size={16} className="mr-2" />
                    Share on x.com
                </DropdownMenuItem>
                <DropdownMenuItem onClick={shareToLinkedIn}>
                    <FaLinkedinIn size={16} className="mr-2" />
                    Share on LinkedIn
                </DropdownMenuItem>
                <DropdownMenuItem onClick={shareToFacebook}>
                    <FaFacebookF size={16} className="mr-2" />
                    Share on Facebook
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

